import { isNumber } from "lodash";
import * as Accordion from "@radix-ui/react-accordion";
import { Icon } from "react-migration/components/Icon";
import { useLocalStorage } from "usehooks-ts";
import { getAttributeNumericValue } from "../utils";
import { BarSparkline } from "./charts/BarSparkline";
import { PropsWithChildren, useEffect } from "react";
import { SingleDesignation } from "react-migration/domains/constraints/typings/applicationTypes/SingleDesignation";
import { OutputAreaAttribute } from "./types";
import { AttributeComparisonTextSummary } from "./AttributeComparisonTextSummary";
import clsx from "clsx";

const LOCAL_STORAGE_KEY_PREFIX = "DemographicsLayerAccordion";

type DesignationsComparisonChartProps = {
  attribute: OutputAreaAttribute;
  designation: SingleDesignation;
  comparableDesignation?: SingleDesignation;
  printable?: boolean;
};

export const DesignationsComparisonChart = ({
  attribute,
  designation,
  comparableDesignation,
  printable = false,
}: DesignationsComparisonChartProps) => {
  const { designation_attributes: attributes } = designation || {};
  const { designation_attributes: comparativeDesignationAttributes } = comparableDesignation || {};
  const summaryNumericValue = getAttributeNumericValue(attributes, attribute.attributeKey);

  const summaryComparativeNumericValue = getAttributeNumericValue(
    comparativeDesignationAttributes,
    attribute.attributeKey
  );

  return (
    <>
      {isNumber(summaryNumericValue) && (
        <>
          <BarSparkline
            valueFormatter={attribute.format}
            value={summaryNumericValue}
            comparativeValue={isNumber(summaryComparativeNumericValue) ? summaryComparativeNumericValue : undefined}
          />
          <div className="flex justify-between items-center gap-x-2 w-full py-2">
            <AttributeComparisonTextSummary
              label={attribute.label}
              value={summaryNumericValue}
              comparativeValue={summaryComparativeNumericValue}
              comparativeDisplayName={comparableDesignation?.display_name}
            />
          </div>
        </>
      )}
    </>
  );
};

type AttributeComparisonSummarySectionProps = PropsWithChildren<{
  heading: string;
  headlineAttribute: OutputAreaAttribute;
  designation: SingleDesignation;
  comparableDesignation?: SingleDesignation;
  printable?: boolean;
}>;

export function AttributeComparisonSummarySection({
  heading,
  headlineAttribute,
  /** The provided designation must have one of these attributes, else it will
   * not render. Should this be the requirement of the children to decide. */
  designation,
  comparableDesignation,
  children,
  printable = false,
}: AttributeComparisonSummarySectionProps) {
  const { attributeKey } = headlineAttribute;
  const [accordionState, setAccordionState] = useLocalStorage<string[]>(
    `${LOCAL_STORAGE_KEY_PREFIX}_${attributeKey}`,
    []
  );

  useEffect(() => {
    if (printable) {
      setAccordionState([attributeKey]);
    }
  }, [printable, attributeKey, setAccordionState]);

  return (
    <Accordion.Root type="multiple" value={accordionState} onValueChange={!printable ? setAccordionState : undefined}>
      <Accordion.Item
        className={clsx("flex flex-col gap-y-3", {
          "print:break-inside-avoid": printable,
        })}
        value={attributeKey}
      >
        <Accordion.Trigger className="flex flex-col group">
          <div className="flex flex-row justify-between items-center gap-x-2 w-full my-2">
            <h1 className="shrink-0 text-sm text-neutral-800 font-semibold">{heading}</h1>
            {!printable && (
              <Icon
                icon="icon-lt-arrow-down-s-line"
                extraClasses="transition-transform group-data-[state=open]:-rotate-180"
              />
            )}
          </div>
          <DesignationsComparisonChart
            attribute={headlineAttribute}
            designation={designation}
            comparableDesignation={comparableDesignation}
            printable={printable}
          />
        </Accordion.Trigger>
        <Accordion.Content
          className={clsx("text-sm gap-3 text-neutral-800 data-[state=closed]:hidden pb-4", {
            "grid grid-cols-[repeat(2,minmax(215px,1fr))] print:justify-start screen:gap-4 print:gap-2": printable,
            "flex flex-col": !printable,
          })}
        >
          {children}
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  );
}
